import React, { useState } from "react";
import { CaretDown, CaretUp, Lightbulb } from "@phosphor-icons/react";

const TIPS = [
  {
    q: "How did you get my email / who are you?",
    a: "Be transparent: 'I compile lists of solar installers across Europe using public sources — company websites, industry directories, and open registries. I noticed [ORG NAME] partners with installers, so I thought you might value the list.' Never lie about the source.",
  },
  {
    q: "How many installers do you have in my country?",
    a: "Give them the exact number from your Overview page. Offer a free 10-installer sample so they can verify data quality before paying.",
  },
  {
    q: "How much does it cost?",
    a: "Company-level contact lists (name + website + info@ email): €80 for 100 leads, €175 for 250, €300 for 500. Country subscription with monthly refresh: €49/month. Verified named-contact upgrade via Hunter: +€1-2/lead. Frame it as 'ROI in one closed deal' — a commercial solar deal is worth thousands.",
  },
  {
    q: "Are the emails verified?",
    a: "Be honest: 'Most contacts are company-level (info@) pulled from public listings — 100% real companies but not individually verified emails. For a €1-2/lead upgrade I can run verified-email enrichment via Hunter.io to get named decision-maker contacts.'",
  },
  {
    q: "How is the data delivered?",
    a: "CSV file over email. Ask their preferred format (CSV, Excel, or push to their CRM like HubSpot/Pipedrive).",
  },
  {
    q: "GDPR / data compliance?",
    a: "The data is B2B company contacts (not personal). Under GDPR, legitimate interest applies for B2B outreach. Provide your source list (public websites, industry directories). If asked, remove any contact on request within 24h.",
  },
  {
    q: "Can we get a discount?",
    a: "Yes for volume or subscription. Try: 'Monthly-refresh subscription for your country: €49/month = €588/year. Compare to €400 one-off for 500 leads — subscription pays back after 8 months and you keep getting fresh data forever.'",
  },
  {
    q: "They ghosted / no reply after 5 days",
    a: "Send a short follow-up: 'Hi [Name], just bumping this to the top of your inbox. Would a 10-min call this week make sense?' If still no reply after 7 more days, close with a 'permission to close file' email — often gets replies.",
  },
];

const PRICING_TIERS = [
  { name: "Sample (free)", detail: "10 real installer contacts", price: "Free" },
  { name: "Small", detail: "100 leads · company-level", price: "€80" },
  { name: "Standard", detail: "250 leads · company-level", price: "€175" },
  { name: "Volume", detail: "500 leads · company-level", price: "€300" },
  { name: "Country pack", detail: "All installers (~200-500)", price: "€200-400" },
  { name: "★ Subscription", detail: "Monthly refresh · one country", price: "€49/mo" },
];

export const SalesPlaybook = () => {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);

  return (
    <div className="border border-black bg-white mb-6" data-testid="sales-playbook">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="playbook-toggle"
        className="w-full flex items-center justify-between p-3 text-left hover:bg-black/[0.02] transition-colors duration-150"
      >
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-[#D4FF00] text-[#0A0A0A] flex items-center justify-center">
            <Lightbulb size={12} weight="bold" />
          </div>
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#525252]" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
              SALES PLAYBOOK
            </div>
            <div className="text-sm" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
              What to say when they reply
            </div>
          </div>
        </div>
        {open ? <CaretUp size={14} weight="bold" /> : <CaretDown size={14} weight="bold" />}
      </button>

      {open && (
        <div className="p-4 border-t border-black/10 space-y-6">
          {/* Pricing tiers */}
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#525252] mb-2" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
              PRICING TIERS (SUGGESTED)
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {PRICING_TIERS.map((t) => (
                <div key={t.name} className="border border-black/20 p-2">
                  <div className="text-[10px] tracking-[0.15em] uppercase text-[#525252]" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
                    {t.name}
                  </div>
                  <div className="text-sm mt-1" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>{t.price}</div>
                  <div className="text-[10px] text-[#525252]" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{t.detail}</div>
                </div>
              ))}
            </div>
          </div>

          {/* FAQ */}
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#525252] mb-2" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
              REPLIES / OBJECTIONS
            </div>
            <div className="border border-black/15">
              {TIPS.map((t, i) => (
                <div key={i} className="border-b border-black/10 last:border-b-0">
                  <button
                    data-testid={`playbook-item-${i}`}
                    onClick={() => setExpanded(expanded === i ? null : i)}
                    className="w-full text-left p-2.5 hover:bg-black/[0.02] flex items-center justify-between gap-2 transition-colors duration-150"
                  >
                    <span className="text-sm" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
                      {t.q}
                    </span>
                    {expanded === i ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />}
                  </button>
                  {expanded === i && (
                    <div className="px-2.5 pb-3 text-sm text-[#0A0A0A]/85 leading-relaxed" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
                      {t.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
